import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { ContractLedger } from '@wsphere/warranties/domain';
import { RatingMatrixService } from '../../services';
import { escapeHtml } from '../../utils/escape-html.utils';

interface Element {
  id?: string;
  dimensionId: string;
  title: string;
  description?: string;
  sortOrder?: number;
  lowerBound?: number;
  upperBound?: number;
  parentId?: string | null;
  unsummedOffset?: boolean;
  includeUpstreamDistributorFees?: boolean;
  isNet?: boolean;
  isOneTime?: boolean;
  recipientRole?: ContractLedger.PartyType | null;
  recipientPartyId?: string | null;
}

interface Dimension {
  id: string;
  title: string;
  isScalar: boolean;
  isSystem?: boolean;
}

function normalizeDimensionTitle(value: string): string {
  return value.toLowerCase().replace(/[\s\-_/]+/g, '');
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-element-dialog',
  templateUrl: './element.dialog.html',
  standalone: false,
})
export class ElementDialog {
  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @Input() element?: Element | null = null;
  @Input() dimension?: Dimension | null = null;
  @Input() existingElements?: Element[] = [];

  @Output() closed = new EventEmitter<boolean>();

  saving = false;

  parentId: string | null = null;
  parentTitle: string | null = null;

  form!: FormGroup;

  // Fee Type only — saved through its own endpoint after the element itself, and only when changed.
  recipient: ContractLedger.RecipientAssignment | null = null;
  private initialRecipientKey = '';

  private boundsValidator = (control: AbstractControl): ValidationErrors | null => {
    const lowerBound = this.toNumberOrNull(control.get('lowerBound')?.value);
    const upperBound = this.toNumberOrNull(control.get('upperBound')?.value);

    if (lowerBound !== null && upperBound !== null && upperBound <= lowerBound) {
      return { boundsInvalid: true };
    }
    return null;
  };

  constructor(
    private formBuilder: FormBuilder,
    private toastSvc: ToastService,
    private http: HttpClient,
    private ratingMatrixSvc: RatingMatrixService
  ) {
    this.form = this.formBuilder.group(
      {
        title: ['', [Validators.required, Validators.maxLength(255)]],
        description: ['', [Validators.maxLength(255)]],
        lowerBound: [null as number | null],
        upperBound: [null as number | null],
        unsummedOffset: [false],
        includeUpstreamDistributorFees: [false],
        isNet: [true],
        isOneTime: [false],
      },
      { validators: this.boundsValidator }
    );
  }

  get isFeeTypeDimension(): boolean {
    return !!this.dimension?.isSystem && normalizeDimensionTitle(this.dimension.title) === 'feetype';
  }

  open(
    element?: Element,
    dimension?: Dimension,
    existingElements?: Element[],
    parentId?: string | null,
    parentTitle?: string | null
  ) {
    this.dimension = dimension || null;
    this.existingElements = existingElements || [];

    this.parentId = parentId ?? null;
    this.parentTitle = parentTitle ?? null;

    if (element) {
      // EDIT mode
      this.form.patchValue({
        title: element.title,
        description: element.description || '',
        lowerBound: element.lowerBound ?? null,
        upperBound: element.upperBound ?? null,
        unsummedOffset: element.unsummedOffset ?? false,
        includeUpstreamDistributorFees: element.includeUpstreamDistributorFees ?? false,
        isNet: element.isNet ?? true,
        isOneTime: element.isOneTime ?? false,
      });

      this.element = element;
      this.recipient = element.recipientRole
        ? { role: element.recipientRole, partyRecordId: element.recipientPartyId ?? null }
        : null;

      this.parentId = null;
      this.parentTitle = null;
    } else {
      // CREATE mode
      this.element = null;
      this.recipient = null;
      this.form.reset({
        unsummedOffset: false,
        includeUpstreamDistributorFees: false,
        isNet: true,
        isOneTime: false,
      });
    }

    this.initialRecipientKey = this.recipientKey(this.recipient);
    this.dialog.open();
  }

  close(fromClose?: boolean) {
    this.form.reset();
    this.saving = false;
    this.parentId = null;
    this.parentTitle = null;

    if (!fromClose) this.dialog.close();
  }

  cancel() {
    this.close();
    this.closed.emit(false);
  }

  save() {
    if (this.form.invalid || !this.dimension) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const value = this.form.value;
    const title = value.title?.trim() || '';

    if (!title) {
      this.form.markAllAsTouched();
      this.saving = false; // Reset saving state
      return;
    }

    const payload: Record<string, unknown> = {
      dimensionId: this.dimension.id,
      title,
      description: value.description?.trim() || null,
    };

    // Scalar fields
    if (this.dimension.isScalar) {
      payload['lowerBound'] = this.toNumberOrNull(value.lowerBound);
      payload['upperBound'] = this.toNumberOrNull(value.upperBound);
    }

    // Fee Type only
    if (this.isFeeTypeDimension) {
      payload['unsummedOffset'] = !!value.unsummedOffset;
      payload['includeUpstreamDistributorFees'] = !!value.includeUpstreamDistributorFees;
      payload['isNet'] = !!value.isNet;
      payload['isOneTime'] = !!value.isOneTime;
    }

    // CREATE mode
    if (!this.element?.id) {
      payload['parentId'] = this.parentId ?? null;
    }

    const request$ = this.element?.id
      ? this.http.patch(`/v1/rating/elements/${this.element.id}`, payload)
      : this.http.post('/v1/rating/elements', payload);

    request$.subscribe({
      next: async (saved: unknown) => {
        const elementId = this.element?.id ?? (saved as { id?: string } | null)?.id;
        if (this.isFeeTypeDimension && elementId && this.recipientKey(this.recipient) !== this.initialRecipientKey) {
          try {
            await this.ratingMatrixSvc.setElementRecipient(elementId, this.recipient);
          } catch (error) {
            this.toastSvc.show({
              type: 'error',
              title: 'Recipient not saved',
              text: escapeHtml(
                (error as { error?: { message?: string } })?.error?.message || 'Unable to save the default recipient.'
              ),
            });
          }
        }

        this.toastSvc.show({
          type: 'success',
          title: this.element?.id ? 'Element updated' : 'Element created',
          text: this.element?.id
            ? `<strong>${title}</strong> has been successfully updated.`
            : `<strong>${title}</strong> has been successfully created.`,
        });

        this.close();
        this.closed.emit(true);
      },
      error: (error: { error?: { message?: string } }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error?.error?.message || 'Unable to save element.',
        });

        this.saving = false;
      },
    });
  }

  private recipientKey(recipient: ContractLedger.RecipientAssignment | null): string {
    return recipient ? `${recipient.role}|${recipient.partyRecordId ?? ''}` : '';
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(num) ? null : num;
  }
}
